import { type ReactElement } from 'react';
import { Icon } from '@/components/atoms/icon';
import { cn } from '@/lib/cn';
import { formatMinutesSeconds } from '@/lib/format-time';
import type { QueueItem } from '@/lib/types';
import { queueRowSubtitle } from '@/lib/practice/queue-row-subtitle';

type QueueRowState = 'done' | 'active' | 'upcoming';

function rowState(index: number, currentIndex: number): QueueRowState {
  if (index < currentIndex) return 'done';
  if (index === currentIndex) return 'active';
  return 'upcoming';
}

export type PracticeQueueRailProps = {
  width: number;
  items: QueueItem[];
  currentIndex: number;
  elapsedSeconds: number;
  plannedSeconds: number;
};

export function PracticeQueueRail({
  width,
  items,
  currentIndex,
  elapsedSeconds,
  plannedSeconds,
}: PracticeQueueRailProps): ReactElement {
  const progressPct = plannedSeconds > 0
    ? Math.min(100, Math.round((elapsedSeconds / plannedSeconds) * 100))
    : 0;

  return (
    <aside
      data-practice-queue-rail
      data-rail-width={String(width)}
      style={{ width: `${width}px` }}
      className="shrink-0 border-r border-[var(--rule)] flex flex-col bg-[var(--bg-surface)] text-[var(--ink)]"
    >
      <div className="h-16 px-5 flex items-center border-b border-[var(--rule)]">
        <div>
          <div className="text-[10px] font-mono tracking-[0.18em] text-[var(--ink-3)] uppercase">Session</div>
          <div className="text-[13px] font-semibold">Today</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto nice-scroll p-3">
        <div className="text-[10px] font-mono tracking-[0.18em] text-[var(--ink-3)] uppercase px-2 pt-2 pb-1">
          Queue · {items.length} {items.length === 1 ? 'tune' : 'tunes'}
        </div>
        <ul role="list" className="space-y-1">
          {items.map((item, index) => {
            const state = rowState(index, currentIndex);
            const subtitle = queueRowSubtitle(item);
            return (
              <li
                key={item.userArrangementId}
                data-queue-row-state={state}
                className={cn(
                  'p-3 rounded-[var(--r-md)] flex items-center gap-3 border',
                  state === 'active'   && 'bg-[var(--brand-wash)] border-[var(--rule)]',
                  state === 'upcoming' && 'border-transparent hover:bg-[var(--bg-soft)]',
                  state === 'done'     && 'border-transparent opacity-70',
                )}
              >
                <div
                  aria-hidden
                  className={cn(
                    'w-8 h-8 rounded-[var(--r-sm)] flex items-center justify-center font-display font-semibold text-xs',
                    state === 'done'     && 'bg-[var(--ok)] text-[var(--bg-surface)]',
                    state === 'active'   && 'bg-[var(--warm)] text-[var(--bg-surface)]',
                    state === 'upcoming' && 'bg-[var(--bg-soft)]',
                  )}
                >
                  {state === 'done' ? <Icon name="check" size={14} /> : item.pieceName.slice(0, 1)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-semibold truncate">{item.pieceName}</div>
                    {item.plannedMinutes !== null && (
                      <span className="shrink-0 text-[10px] font-mono bg-[var(--bg-soft)] rounded px-1.5 py-0.5">
                        {item.plannedMinutes} min
                      </span>
                    )}
                  </div>
                  {subtitle && <div className="text-[10px] text-[var(--ink-3)] truncate">{subtitle}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="p-4 border-t border-[var(--rule)]">
        <div className="flex items-center justify-between text-[11px] text-[var(--ink-3)] mb-2 font-mono uppercase tracking-wider">
          <span>Session</span>
          <span>
            {formatMinutesSeconds(elapsedSeconds)} / {formatMinutesSeconds(plannedSeconds)}
          </span>
        </div>
        <div className="h-1 rounded-full bg-[var(--rule)] overflow-hidden">
          <div className="h-full bg-[var(--brand)]" style={{ width: `${progressPct}%` }} />
        </div>
      </div>
    </aside>
  );
}
